import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthError, confirmSignUp, resendCode, signIn, signUp } from '../lib/auth';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/store';
import { redeemClientInvite } from '../lib/invite';
import type { MemberRole } from '../lib/types';
import { Card, Notice } from '../components/Shell';
import { TextField } from '../components/Field';

/**
 * Sign-up is three moves in one screen: create the login, confirm the email,
 * then join this business as a client. Splitting them across pages loses
 * people, and the last step is what actually makes the portal usable.
 *
 * The host decides which account this is. On a sitter's own address there is a
 * tenant, so the new user is registered as that sitter's client. On the
 * platform host there is none, and a client row cannot exist without a tenant
 * to belong to, so the only thing left to create is a business: the copy says
 * so rather than promising a pet file this path cannot make.
 *
 * Arriving from an invitation link changes the last step only. The invitation
 * already names a business and a file to attach to, so it is redeemed instead
 * of registering fresh, and the address is fixed: the API matches the token
 * against the signed-in caller's email, so signing up as somebody else here
 * would produce an invitation that refuses to open with no way to tell why.
 */
export function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant, load, adoptMembership } = useSession();

  const invited = (location.state ?? null) as {
    inviteToken?: string;
    email?: string;
    name?: string;
  } | null;
  const inviteToken = invited?.inviteToken;

  const [step, setStep] = useState<'details' | 'confirm'>('details');
  const [name, setName] = useState(invited?.name ?? '');
  const [email, setEmail] = useState(invited?.email ?? '');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const describe = (err: unknown): string => {
    if (err instanceof AuthError || err instanceof ApiError) return err.message;
    return 'Something went wrong. Try again.';
  };

  const onCreate = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await signUp(email, password, name);
        setNotice(`We sent a six-digit code to ${email}.`);
        setStep('confirm');
      } catch (err) {
        // An existing unconfirmed account should continue, not dead-end.
        if (err instanceof AuthError && err.code === 'UsernameExistsException') {
          setError('An account already exists for that email. Sign in instead.');
        } else {
          setError(describe(err));
        }
      } finally {
        setBusy(false);
      }
    })();
  };

  const onConfirm = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await confirmSignUp(email, code);
        await signIn(email, password);
        await api('/auth/bootstrap', { method: 'POST', body: { name, phone: mobile || undefined } });

        // An invitation is the whole answer to which business and which file,
        // so it replaces the registration below rather than running after it.
        if (inviteToken) {
          await load();
          const { path, url } = await redeemClientInvite(inviteToken, { fullName: name, mobile });
          if (url) window.location.assign(url);
          else navigate(path, { replace: true });
          return;
        }

        // Join this business as a client, so the portal has somewhere to go.
        const registered = tenant
          ? await api<{ membership: { role: MemberRole; clientId?: string } }>(
              `/tenants/${tenant.tenantId}/clients/register`,
              { method: 'POST', body: { fullName: name, mobile } }
            )
          : null;

        await load();

        // After load, not before: load replaces the membership list with what
        // the index returns, which for a moment after a write is a list without
        // the row just written. The response to that write is authoritative, so
        // it is applied last and a new client is never turned away from the
        // portal they just joined.
        if (tenant && registered) {
          adoptMembership({
            tenantId: tenant.tenantId,
            slug: tenant.slug,
            businessName: tenant.businessName,
            branding: tenant.branding,
            role: registered.membership.role,
            clientId: registered.membership.clientId,
            status: 'active',
          });
        }

        navigate(tenant ? '/portal' : '/start-a-business', { replace: true });
      } catch (err) {
        setError(describe(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="mx-auto max-w-md px-5 py-16">
      <h1 className="mb-2 text-3xl">
        {step === 'confirm'
          ? 'Confirm your email'
          : tenant || inviteToken
            ? 'Start your pet’s file'
            : 'Set up your sitting business'}
      </h1>
      <p className="mb-8 text-[color:var(--color-ink-soft)]">
        {step === 'confirm'
          ? 'Enter the code we emailed you.'
          : tenant || inviteToken
            ? `Create an account with ${tenant?.businessName ?? 'your sitter'}. It takes a minute.`
            : 'This creates a sitter account, and the next step is your business. Pet owners sign up on their own sitter’s page instead, using the link the sitter sent them.'}
      </p>

      {notice && (
        <div className="mb-5">
          <Notice tone="ok">{notice}</Notice>
        </div>
      )}
      {error && (
        <div className="mb-5">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <Card className="p-6">
        {step === 'details' ? (
          <form onSubmit={onCreate} className="space-y-5">
            <TextField label="Your name" value={name} onChange={setName} autoComplete="name" required />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              readOnly={Boolean(invited?.email)}
              hint={invited?.email ? 'The address your invitation was sent to.' : undefined}
              required
            />
            <TextField
              label="Mobile"
              type="tel"
              value={mobile}
              onChange={setMobile}
              autoComplete="tel"
              hint={
                tenant || inviteToken
                  ? 'Used for updates about your pet while they are in care.'
                  : 'Shown to your clients so they can reach you.'
              }
              required
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              hint="At least 12 characters, with an uppercase letter, a lowercase letter and a number."
              required
            />
            <button type="submit" className="btn btn-primary w-full" disabled={busy}>
              {busy ? 'Creating' : 'Create my account'}
            </button>
            <p className="pt-1 text-center text-sm text-[color:var(--color-ink-soft)]">
              Already have one? <Link to="/sign-in" className="underline">Sign in</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={onConfirm} className="space-y-5">
            <TextField label="Confirmation code" value={code} onChange={setCode} autoComplete="one-time-code" required />
            <button type="submit" className="btn btn-primary w-full" disabled={busy}>
              {busy ? 'Confirming' : 'Confirm and continue'}
            </button>
            <button
              type="button"
              className="btn btn-quiet w-full"
              disabled={busy}
              onClick={() => {
                void resendCode(email)
                  .then(() => setNotice('We sent another code.'))
                  .catch((err) => setError(describe(err)));
              }}
            >
              Send another code
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
